import type { PushMessagingAdapter, PushTokenStore } from './push-client.js';
import type { PushPlatform } from './push-state.js';

/**
 * Binders from an app's own Firebase instance to the push client's adapter
 * interfaces. The app passes `firebase/messaging`'s functions in, so this
 * package needs no Firebase dependency and all three apps share one wiring.
 */

/** The parts of an FCM `MessagePayload` the foreground toast uses. */
export interface FcmPayloadLike {
  readonly notification?: { readonly title?: string; readonly body?: string };
  readonly data?: Readonly<Record<string, string>>;
  readonly fcmOptions?: { readonly link?: string };
}

export interface FirebaseMessagingFns<M> {
  isSupported(): Promise<boolean>;
  getToken(
    messaging: M,
    options: { vapidKey: string; serviceWorkerRegistration: ServiceWorkerRegistration },
  ): Promise<string>;
  deleteToken(messaging: M): Promise<boolean>;
  onMessage(messaging: M, next: (payload: FcmPayloadLike) => void): () => void;
}

export function firebaseMessagingAdapter<M>(
  fns: FirebaseMessagingFns<M>,
  messaging: Promise<M | null>,
  vapidKey: string | undefined,
): PushMessagingAdapter {
  return {
    isSupported: () => fns.isSupported(),
    async getToken(serviceWorkerRegistration) {
      const m = await messaging;
      if (!m || !vapidKey) return '';
      return fns.getToken(m, { vapidKey, serviceWorkerRegistration });
    },
    async deleteToken() {
      const m = await messaging;
      return m ? fns.deleteToken(m) : false;
    },
    async onForegroundMessage(cb) {
      const m = await messaging;
      if (!m) return () => undefined;
      return fns.onMessage(m, (payload) => {
        const data = payload.data ?? {};
        cb({
          title: payload.notification?.title ?? data['title'] ?? null,
          body: payload.notification?.body ?? data['body'] ?? '',
          deepLink: data['deepLink'] ?? payload.fcmOptions?.link ?? null,
          tenantId: data['tenantId'] ?? null,
          receivedAt: Date.now(),
        });
      });
    },
  };
}

interface TimestampParts {
  readonly seconds: number;
  readonly nanoseconds: number;
}

/** Minimal shape of an app's `users/{uid}/fcmTokens` repo. */
export interface FcmTokenRepoLike<TS> {
  get(id: string): Promise<{ readonly createdAt: TimestampParts } | null>;
  set(
    id: string,
    value: { token: string; platform: PushPlatform; createdAt: TS; lastUsedAt: TS },
  ): Promise<void>;
  delete(id: string): Promise<void>;
}

/**
 * `users/{uid}/fcmTokens/{tokenId}` writer: keeps `createdAt` (when this device
 * first opted in), bumps `lastUsedAt`. `createdAt` is rebuilt through
 * `fromParts` so a value read back through a repo is written as a REAL
 * Firestore Timestamp — Firestore rejects a look-alike (ADR-0027).
 */
export function firestoreTokenStore<TS>(opts: {
  readonly repoFor: (uid: string) => FcmTokenRepoLike<TS>;
  readonly now: () => TS;
  readonly fromParts: (seconds: number, nanoseconds: number) => TS;
}): PushTokenStore {
  return {
    async upsert(uid, docId, token, platform) {
      const repo = opts.repoFor(uid);
      const now = opts.now();
      const existing = await repo.get(docId).catch(() => null);
      const createdAt = existing
        ? opts.fromParts(existing.createdAt.seconds, existing.createdAt.nanoseconds)
        : now;
      await repo.set(docId, { token, platform, createdAt, lastUsedAt: now });
    },
    async remove(uid, docId) {
      await opts.repoFor(uid).delete(docId);
    },
  };
}
